/**
 * Configuration Service - API calls for configuration CRUD operations
 *
 * PURPOSE:
 * Provides a clean interface for configuration API operations with automatic hydration.
 * Configurations require a hydrated ProjectData for bond series reconstruction.
 *
 * PATTERN:
 * - Returns `data | null` (null on any error)
 * - Uses logger for error reporting
 * - Hydrates API responses before returning
 * - Requires ProjectData for context (bond series depend on project dates)
 *
 * API ENDPOINTS:
 * - GET    /api/projects/:projectId/configurations              → List configs
 * - POST   /api/projects/:projectId/configurations              → Create config
 * - GET    /api/projects/:projectId/configurations/:configId    → Get config
 * - PUT    /api/projects/:projectId/configurations/:configId    → Update config
 * - DELETE /api/projects/:projectId/configurations/:configId    → Delete config
 */

import type { ConfigurationInfo, ConfigurationInputs } from '@shared/models';
import type { ProjectData } from '~/hooks/useProjectStore';
import { logger } from '~/services/logging';
import type { Configuration } from '~/types';
import {
  dehydrateConfigurationInputs,
  hydrateConfiguration,
  hydrateConfigurations,
} from './hydration-service';

// ============================================
// Types
// ============================================

export interface CreateConfigurationParams {
  name?: string;
  inputs: ConfigurationInputs;
}

export interface UpdateConfigurationParams {
  name?: string;
  inputs?: ConfigurationInputs;
}

// ============================================
// API Methods
// ============================================

/**
 * Get all configurations for a project (hydrated)
 * Requires hydrated ProjectData for bond series reconstruction
 */
export async function getConfigurations(
  projectId: string,
  project: ProjectData
): Promise<Configuration[] | null> {
  try {
    const response = await fetch(`/api/projects/${projectId}/configurations`);

    if (!response.ok) {
      logger.L2.debug('[ConfigurationService] Failed to fetch configurations', {
        context: 'api:configurations',
        projectId,
        status: response.status,
      });
      return null;
    }

    const { configurations } = (await response.json()) as { configurations: ConfigurationInfo[] };

    // Hydrate all configurations (reconstruct bond series from project dates)
    return hydrateConfigurations(configurations, project);
  } catch (error) {
    logger.L2.debug('[ConfigurationService] Error fetching configurations', {
      context: 'api:configurations',
      projectId,
      error: String(error),
    });
    return null;
  }
}

/**
 * Get a single configuration by ID (hydrated)
 * Requires hydrated ProjectData for bond series reconstruction
 */
export async function getConfiguration(
  projectId: string,
  configId: string,
  project: ProjectData
): Promise<Configuration | null> {
  try {
    const response = await fetch(`/api/projects/${projectId}/configurations/${configId}`);

    if (!response.ok) {
      if (response.status === 404) {
        logger.L2.debug('[ConfigurationService] Configuration not found', {
          context: 'api:configurations',
          projectId,
          configId,
        });
      } else {
        logger.L2.debug('[ConfigurationService] Failed to fetch configuration', {
          context: 'api:configurations',
          projectId,
          configId,
          status: response.status,
        });
      }
      return null;
    }

    const { configuration } = (await response.json()) as { configuration: ConfigurationInfo };

    return hydrateConfiguration(configuration, project);
  } catch (error) {
    logger.L2.debug('[ConfigurationService] Error fetching configuration', {
      context: 'api:configurations',
      projectId,
      configId,
      error: String(error),
    });
    return null;
  }
}

/**
 * Create a new configuration
 * Returns the created configuration (hydrated)
 */
export async function createConfiguration(
  projectId: string,
  params: CreateConfigurationParams,
  project: ProjectData
): Promise<Configuration | null> {
  try {
    const response = await fetch(`/api/projects/${projectId}/configurations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      if (response.status === 404) {
        logger.L2.debug('[ConfigurationService] Project not found for configuration creation', {
          context: 'api:configurations',
          projectId,
        });
      } else {
        logger.L2.debug('[ConfigurationService] Failed to create configuration', {
          context: 'api:configurations',
          projectId,
          status: response.status,
        });
      }
      return null;
    }

    const { configuration } = (await response.json()) as { configuration: ConfigurationInfo };

    return hydrateConfiguration(configuration, project);
  } catch (error) {
    logger.L2.debug('[ConfigurationService] Error creating configuration', {
      context: 'api:configurations',
      projectId,
      error: String(error),
    });
    return null;
  }
}

/**
 * Update an existing configuration
 * Returns the updated configuration (hydrated)
 */
export async function updateConfiguration(
  projectId: string,
  configId: string,
  params: UpdateConfigurationParams,
  project: ProjectData
): Promise<Configuration | null> {
  try {
    const response = await fetch(`/api/projects/${projectId}/configurations/${configId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      if (response.status === 404) {
        logger.L2.debug('[ConfigurationService] Configuration not found for update', {
          context: 'api:configurations',
          projectId,
          configId,
        });
      } else {
        logger.L2.debug('[ConfigurationService] Failed to update configuration', {
          context: 'api:configurations',
          projectId,
          configId,
          status: response.status,
        });
      }
      return null;
    }

    const { configuration } = (await response.json()) as { configuration: ConfigurationInfo };

    return hydrateConfiguration(configuration, project);
  } catch (error) {
    logger.L2.debug('[ConfigurationService] Error updating configuration', {
      context: 'api:configurations',
      projectId,
      configId,
      error: String(error),
    });
    return null;
  }
}

/**
 * Delete a configuration
 * Returns true on success, false on failure
 */
export async function deleteConfiguration(projectId: string, configId: string): Promise<boolean> {
  try {
    const response = await fetch(`/api/projects/${projectId}/configurations/${configId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      if (response.status === 404) {
        logger.L2.debug('[ConfigurationService] Configuration not found for delete', {
          context: 'api:configurations',
          projectId,
          configId,
        });
      } else {
        logger.L2.debug('[ConfigurationService] Failed to delete configuration', {
          context: 'api:configurations',
          projectId,
          configId,
          status: response.status,
        });
      }
      return false;
    }

    return true;
  } catch (error) {
    logger.L2.debug('[ConfigurationService] Error deleting configuration', {
      context: 'api:configurations',
      projectId,
      configId,
      error: String(error),
    });
    return false;
  }
}

// ============================================
// Convenience Methods
// ============================================

/**
 * Save a configuration (dehydrates before sending)
 * Extracts minimal storage format from full Configuration
 */
export async function saveConfiguration(
  projectId: string,
  config: Configuration,
  project: ProjectData,
  bondDurations?: Map<string, number>
): Promise<Configuration | null> {
  const inputs = dehydrateConfigurationInputs(config, project, bondDurations);

  // Try to update first
  const updateResult = await updateConfiguration(
    projectId,
    config.id,
    { name: config.name, inputs },
    project
  );

  if (updateResult) {
    return updateResult;
  }

  // If update failed (404), create new
  return createConfiguration(projectId, { name: config.name, inputs }, project);
}

/**
 * Load project with all its configurations (convenience method)
 * Fetches project, hydrates it, then fetches and hydrates all configurations
 */
export async function loadProjectWithConfigurations(
  projectId: string
): Promise<{ project: ProjectData; configurations: Configuration[] } | null> {
  // Import here to avoid circular dependency
  const { getProject } = await import('./project-service');

  const project = await getProject(projectId);
  if (!project) {
    return null;
  }

  const configurations = await getConfigurations(projectId, project);
  if (!configurations) {
    // Return project even if configurations failed to load
    return { project, configurations: [] };
  }

  return { project, configurations };
}

// ============================================
// Export as namespace-like object (optional)
// ============================================

export const ConfigurationService = {
  getConfigurations,
  getConfiguration,
  createConfiguration,
  updateConfiguration,
  deleteConfiguration,
  saveConfiguration,
  loadProjectWithConfigurations,
};
